import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { useChangeOwnPassword } from "@/hooks/useInternalUsers";
import { PASSWORD_HINT, passwordField } from "@/lib/validation";
import { zodResolver } from "@hookform/resolvers/zod";
import { Eye, EyeOff } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

const schema = z
  .object({
    oldPassword: z.string().min(1, "Enter your current password"),
    newPassword: passwordField(),
    confirmPassword: z.string(),
  })
  .refine((d) => d.newPassword === d.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  });

type FormValues = z.infer<typeof schema>;

const FIELDS = [
  {
    name: "oldPassword",
    label: "Current Password",
    testId: "input-current-password",
  },
  { name: "newPassword", label: "New Password", testId: "input-new-password" },
  {
    name: "confirmPassword",
    label: "Confirm New Password",
    testId: "input-confirm-new-password",
  },
] as const;

/** EVO-88: changing your own password requires the current one. */
export function ChangePasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { toast } = useToast();
  const changePassword = useChangeOwnPassword();
  const [visible, setVisible] = useState<Record<string, boolean>>({});
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { oldPassword: "", newPassword: "", confirmPassword: "" },
  });

  const close = (next: boolean) => {
    if (!next) {
      form.reset();
      setVisible({});
    }
    onOpenChange(next);
  };

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await changePassword.mutateAsync(values);
      toast({
        title: "Password changed",
        description: "You were signed out on your other devices.",
      });
      close(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Update failed";
      // Server messages: "Current password is incorrect" and the reuse error
      // "New password must be different from your current password."
      if (/must be different/i.test(message)) {
        form.setError("newPassword", { message });
      } else if (/current password is incorrect/i.test(message)) {
        form.setError("oldPassword", { message });
      } else {
        toast({
          title: "Couldn’t change password",
          description: message,
          variant: "destructive",
        });
      }
    }
  });

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>
            Enter your current password, then choose a new one.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4">
            {FIELDS.map((f) => (
              <FormField
                key={f.name}
                control={form.control}
                name={f.name}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{f.label}</FormLabel>
                    <div className="relative flex items-center">
                      <FormControl>
                        <Input
                          type={visible[f.name] ? "text" : "password"}
                          autoComplete={
                            f.name === "oldPassword"
                              ? "current-password"
                              : "new-password"
                          }
                          className="pr-10"
                          data-testid={f.testId}
                          {...field}
                        />
                      </FormControl>
                      <button
                        type="button"
                        aria-label={
                          visible[f.name] ? "Hide password" : "Show password"
                        }
                        className="absolute right-3 text-muted-foreground hover:text-foreground transition-colors"
                        onClick={() =>
                          setVisible((v) => ({ ...v, [f.name]: !v[f.name] }))
                        }
                      >
                        {visible[f.name] ? (
                          <EyeOff className="h-4 w-4" />
                        ) : (
                          <Eye className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                    {f.name === "newPassword" && (
                      <FormDescription className="text-xs">
                        {PASSWORD_HINT}
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
            ))}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => close(false)}
                disabled={changePassword.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={changePassword.isPending}
                data-testid="button-confirm-change-password"
              >
                {changePassword.isPending ? "Changing..." : "Change Password"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
